'use client'

import {
    ProductHero,
    Statement,
    ScenarioList,
    FlowStrip,
    FeatureCard,
    NextSteps,
    ProductCta,
    RelatedCases,
} from '@/components/platform/v2/ProductKit'
import { Section, SectionHeading } from '@/components/platform/v2/Section'
import { PipelineMock } from '@/components/platform/v2/Mocks'

const ROLES = [
    { role: 'Владелец', access: 'Видит бизнес целиком: продажи, клиентов, команду.' },
    { role: 'Менеджер', access: 'Свои заявки, сделки, задачи и переписку с клиентом.' },
    { role: 'Администратор', access: 'Настройки, справочники, каталог, доступы.' },
    { role: 'Поддержка', access: 'Обращения и чаты без доступа к финансам.' },
    { role: 'Контент-менеджер', access: 'Каталог, страницы, баннеры, материалы.' },
]

const CrmPageComponent = () => {
    return (
        <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
            <ProductHero
                eyebrow="CRM и клиенты"
                titleLines={['Не просто контакт.', 'Вся история клиента.']}
                lead="Заявки, сделки, задачи, сообщения, заказы, документы и действия клиента собираются вокруг одного рабочего контекста."
                primary={{ label: 'Собрать CRM под мой процесс', to: '/signin?focus=crm' }}
                secondary={{ label: 'Обзор платформы', to: '/platform' }}
                visual={
                    <img
                        src={'/assets/images/platform/crm-mascot-hero.png'}
                        alt="Маскот VCENTRE.IT в очках с фразой «Не очень одинок»"
                        width={768}
                        height={768}
                        loading="eager"
                        className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
                    />
                }
            />

            <Section tone="quiet">
                <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                    <div className="flex flex-col gap-4">
                        <p className="eyebrow">Главная мысль</p>
                        <h2 className="display-2">
                            Один клиент.
                            <br />
                            <span className="text-primary">Одна история.</span>
                        </h2>
                        <p className="lede">
                            Обычно данные о клиенте живут в пяти местах: заявка — в почте, переписка — в мессенджере, оплата — в банке,
                            бонусы — в таблице. В VCENTRE.ONE это одна карточка.
                        </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        {[
                            'Контакты и реквизиты',
                            'Последние действия',
                            'Заявки и сделки',
                            'Заказы и оплаты',
                            'Сообщения и чаты',
                            'Задачи команды',
                            'Документы и файлы',
                            'Лояльность и история',
                        ].map((i) => (
                            <div
                                key={i}
                                className="rounded-xl border border-border/60 bg-[hsl(var(--deep-black))] px-4 py-3 text-sm font-semibold text-foreground/90"
                            >
                                {i}
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* ОФЛАЙН */}
            <Section>
                <SectionHeading
                    align="left"
                    eyebrow="Офлайн-клиент — полноценный клиент"
                    title="Клиенту не обязательно быть в приложении"
                    text="Менеджер ведёт клиента сам, а когда клиент зарегистрируется — его цифровой профиль связывается с уже накопленной историей."
                />
                <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_0.9fr]">
                    <ScenarioList
                        steps={[
                            { title: 'Позвонил клиент', text: 'менеджер отвечает и сразу создаёт контакт' },
                            { title: 'Создаётся сделка', text: 'с ответственным и суммой' },
                            { title: 'Ставится задача', text: 'чтобы никто не забыл о следующем шаге' },
                            { title: 'Добавляется комментарий', text: 'договорённости остаются в системе, а не в голове' },
                            { title: 'Ведётся история', text: 'визиты, оплаты, документы' },
                            { title: 'Клиент регистрируется позже', text: 'профиль связывается с существующей историей' },
                        ]}
                    />
                    <div className="surface flex flex-col justify-center gap-4">
                        <p className="text-lg font-extrabold text-foreground">Почему это важно</p>
                        <p className="body-text">
                            Большинство систем начинают работать только тогда, когда клиент что-то нажал. Реальный бизнес так не живёт:
                            часть клиентов звонит, приходит лично или знакомится на мероприятии.
                        </p>
                        <p className="body-text">В VCENTRE.ONE такой клиент — не исключение из процесса, а обычная запись в общей базе.</p>
                    </div>
                </div>
            </Section>

            {/* ЗАЯВКИ И СДЕЛКИ */}
            <Section tone="quiet">
                <SectionHeading
                    align="left"
                    eyebrow="Заявки и сделки"
                    title="Не доска со стикерами, а процесс продажи"
                    text="На каждом этапе рядом с клиентом происходят связанные действия: задача, документ, сообщение, оплата."
                />
                <div className="mt-8 flex flex-col gap-6">
                    <PipelineMock />
                    <FlowStrip
                        compact
                        steps={[
                            { title: 'Новая заявка' },
                            { title: 'Квалификация' },
                            { title: 'Предложение' },
                            { title: 'Счёт' },
                            { title: 'Оплата', accent: true },
                            { title: 'Завершение' },
                        ]}
                    />
                </div>
            </Section>

            {/* ЗАДАЧИ */}
            <Section>
                <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
                    <div className="flex flex-col gap-4">
                        <p className="eyebrow">Задачи</p>
                        <h2 className="display-2">Задача живёт рядом с клиентом</h2>
                        <p className="lede">
                            Сделка перешла на следующий этап — у менеджера автоматически появилась задача. Это не отдельный таск-трекер:
                            задача открывается вместе с клиентом, сделкой и перепиской.
                        </p>
                    </div>
                    <FlowStrip
                        steps={[
                            { title: 'Сделка сменила этап', note: 'например, отправили счёт' },
                            { title: 'Создалась задача', note: '«проверить оплату через 2 дня»', accent: true },
                            { title: 'Менеджер видит контекст', note: 'клиент, история, документы' },
                        ]}
                    />
                </div>
            </Section>

            {/* РОЛИ */}
            <Section tone="quiet">
                <SectionHeading
                    align="left"
                    eyebrow="Команда"
                    title="Сотруднику не нужен доступ ко всей системе"
                    text="Права настраиваются под структуру компании: каждый видит свою часть работы."
                />
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {ROLES.map((r) => (
                        <FeatureCard key={r.role} title={r.role} text={r.access} />
                    ))}
                </div>
            </Section>

            {/* СВЯЗЬ */}
            <Section>
                <SectionHeading
                    eyebrow="Связь с платформой"
                    title="CRM не существует отдельно"
                    text="Всё, что происходит в других частях платформы, приходит в карточку клиента."
                />
                <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                        { t: 'Приложение', v: 'заявки, заказы, записи, бонусы' },
                        { t: 'Сайт', v: 'формы, заявки, квизы' },
                        { t: 'Чаты', v: 'переписка в профиле клиента' },
                        { t: 'Заказы и оплаты', v: 'статусы и документы' },
                        { t: 'Лояльность', v: 'баллы, уровни, рефералы' },
                        { t: 'Автоматизация', v: 'статусы, задачи, уведомления' },
                        { t: 'Интеграции', v: '1С, платежи, внешние системы' },
                        { t: 'Аналитика', v: 'воронка и работа команды' },
                    ].map((i) => (
                        <div key={i.t} className="rounded-2xl border border-border/60 bg-[hsl(var(--deep-black))] p-5">
                            <p className="text-sm font-bold text-primary">{i.t}</p>
                            <p className="mt-1 text-sm text-muted-foreground">{i.v}</p>
                        </div>
                    ))}
                </div>
            </Section>

            <Section tone="quiet">
                <Statement>Заявка пришла — менеджер уже видит клиента, историю и следующий шаг.</Statement>
            </Section>

            <Section>
                <RelatedCases
                    slugs={['private-clinic', 'dobrodomik']}
                    text="Проекты, где работа с клиентом переехала из таблиц и переписок в один контур."
                />
            </Section>

            <Section tone="quiet">
                <NextSteps
                    items={[
                        { to: '/platform/communications', text: 'Посмотрите, как сообщения клиента связаны с CRM' },
                        { to: '/platform/automation-ai', text: 'Автоматизируйте следующие действия по сделке' },
                    ]}
                />
            </Section>

            <Section>
                <ProductCta
                    title="Как будет работать CRM именно у вас?"
                    text="Мы покажем, как собрать этот процесс в VCENTRE.ONE."
                    buttonLabel="Показать мой процесс"
                    to="/signin?focus=crm&source=crm-bottom"
                    secondary={{ to: '/platform/cases', label: 'Посмотреть кейсы' }}
                    visual={
                        <img
                            src={'/assets/images/platform/crm-mascot-cta.png'}
                            alt="Маскот VCENTRE.IT с ракетой и фразой «Запускаем быстро»"
                            width={768}
                            height={768}
                            loading="lazy"
                            className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
                        />
                    }
                />
            </Section>
        </div>
    )
}

export default CrmPageComponent
